/**
 * Shared outcome-vocabulary rendering for `CampaignOutcomeItem[]` (action-breakdown
 * / "One outcome vocabulary across fan-out actions", "An unrecognised outcome never
 * presents a committed action as failed"). Confirmation's pre-commit preview and, in
 * future, any other fan-out action's breakdown or result all report the SAME per-item
 * shape — this module is the one place that turns an outcome literal into a label, a
 * badge tone, and (where the outcome carries a reason or a position) a business-
 * readable detail line.
 *
 * Deliberately separate from record-sale-columns.tsx's `SaleBreakdownRow`/
 * `useRecordSaleColumns`: that module renders the record-sale dialog's OWN pre-commit
 * preview shape (`SalePreviewEntry`, grouped one row per nośnik with a `periods[]`
 * array and a 3-way `alreadySold | willClose | skipped` outcome derived from
 * `currentlyAccepted`/`reservationStatus`) and stays untouched by this change
 * (proposal.md: "The sale dialog's existing breakdown becomes the shared
 * presentation, with no change to how it behaves"). This module instead renders the
 * shared `CampaignOutcomeItem` union directly, one row per (mediaUnitId, dateFrom,
 * dateTo) entry — action-breakdown's own "Counts are per nośnik, not per window"
 * scenario requires the LISTING to show every nośnik-and-window entry even though the
 * aggregate COUNT collapses by nośnik, which rules out sale dialog's per-nośnik
 * period-grouping for this vocabulary.
 */
import * as React from 'react';
import { Badge, type BadgeProps } from '@ooh/ui/components/badge';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import type { TFunction } from 'i18next';
import type { CampaignOutcomeItem } from '@ooh/contracts';
import { formatDisplayDateRange } from '../../../lib/date-format';
import { buildOrderedVisibleColumns } from '../../../lib/ordered-visible-columns';
import {
  buildUnitsTabColumns,
  useOfferTableColumnPreferences,
} from '../units-tab/units-tab-columns';
import type { OfferUnitItem } from '../units-tab/units-tab-columns';
import { OFFER_UNIT_COST_COLUMN_IDS } from '../units-tab/lib/offer-unit-cost-columns';
import { OFFER_UNIT_PRICE_COLUMN_IDS } from '../units-tab/lib/offer-unit-price-columns';
import { describeBatchOutcomeFailure } from '@ooh/screens';

/** i18next's typed `TFunction<'offers'>` only accepts key literals it can statically
 * verify against the offers.json shape — a key assembled at runtime from
 * `OUTCOME_LABEL_KEY[outcome]` (or handed to `describeBatchOutcomeFailure`, which
 * itself expects this same loose shape) needs the untyped call signature instead, the
 * same cast `lockOutcomeFailedDetail` (offer-versions/lock-reservations-dialog.tsx)
 * already applies for the identical reason. */
export type LooseT = (key: string, options?: Record<string, unknown>) => string;

/** One `CampaignOutcomeItem` entry joined against the offer's own unit list, so the
 * breakdown can render the caller's own configured offer-table columns alongside it
 * (offer-approval / "Approval Action UI in Offer Workspace": "list nośniki using the
 * caller's own configured columns, as the sale dialog already does"). */
export type OutcomeBreakdownRow = OfferUnitItem & CampaignOutcomeItem;

/** Every outcome the shared vocabulary declares, in display order — 'unrecognized' is
 * the catch-all `CampaignOutcomeItemSchema` parses an outcome literal this build does
 * not know into (packages/contracts/src/schemas/campaign-sale.ts). */
const OUTCOME_DISPLAY_ORDER: CampaignOutcomeItem['outcome'][] = [
  'newly_reserved',
  'raised_from_preliminary',
  'already_secured',
  'queued',
  'not_secured',
  'sold',
  'already_sold',
  'skipped_not_accepted',
  'not_closed',
  'unrecognized',
];

export const OUTCOME_LABEL_KEY: Record<CampaignOutcomeItem['outcome'], string> = {
  newly_reserved: 'workspace.header.outcomeBreakdown.group.newlyReserved',
  raised_from_preliminary: 'workspace.header.outcomeBreakdown.group.raisedFromPreliminary',
  already_secured: 'workspace.header.outcomeBreakdown.group.alreadySecured',
  queued: 'workspace.header.outcomeBreakdown.group.queued',
  not_secured: 'workspace.header.outcomeBreakdown.group.notSecured',
  sold: 'workspace.header.outcomeBreakdown.group.sold',
  already_sold: 'workspace.header.outcomeBreakdown.group.alreadySold',
  skipped_not_accepted: 'workspace.header.outcomeBreakdown.group.skippedNotAccepted',
  not_closed: 'workspace.header.outcomeBreakdown.group.notClosed',
  unrecognized: 'workspace.header.outcomeBreakdown.group.unrecognized',
};

/** An outcome the action could not resolve is styled destructive; a queued entry is an
 * ordinary outcome and SHALL NOT carry error styling (action-breakdown / "One outcome
 * vocabulary across fan-out actions" — "An entry placed in the waiting queue is a
 * normal outcome and SHALL NOT be styled as an error"); 'unrecognized' is styled
 * neutrally (`secondary`), never destructive, since a committed action carrying it is
 * still a success. */
export const OUTCOME_BADGE_VARIANT: Record<CampaignOutcomeItem['outcome'], BadgeProps['variant']> =
  {
    newly_reserved: 'success',
    raised_from_preliminary: 'success',
    already_secured: 'info',
    queued: 'warning',
    not_secured: 'destructive',
    sold: 'success',
    already_sold: 'info',
    skipped_not_accepted: 'warning',
    not_closed: 'destructive',
    unrecognized: 'secondary',
  };

/**
 * Business-readable reason for a `not_secured`/`not_closed` entry, reusing
 * `describeBatchOutcomeFailure` (reservations feature) — the same errorCode ->
 * business-language mapping `lockOutcomeFailedDetail`
 * (offer-versions/lock-reservations-dialog.tsx) and `unified-select-popover.tsx`
 * already reuse for their own "failed" result rows, so a raw error identifier never
 * leaks and an unmapped code falls back to that function's own generic message
 * (action-breakdown / "One outcome vocabulary across fan-out actions" — "SHALL name
 * the reason in the caller's language... SHALL NOT print a raw error identifier").
 */
function describeOutcomeReason(
  item: Extract<CampaignOutcomeItem, { outcome: 'not_secured' } | { outcome: 'not_closed' }>,
  t: TFunction<'offers'>,
): string {
  const minWindowDays = item.outcome === 'not_secured' ? item.minWindowDays : undefined;
  const selectedDays = item.outcome === 'not_secured' ? item.selectedDays : undefined;
  return describeBatchOutcomeFailure(
    { errorCode: item.reason, minWindowDays, selectedDays },
    t as LooseT,
    'reservations:toasts',
  );
}

/** The detail line shown under an entry's outcome badge: the queue position, the
 * business reason it could not be resolved, or a reload invitation for an entry this
 * build does not recognise. Every other outcome carries no reason — it is not a
 * problem, so nothing is shown. */
export function getOutcomeDetail(
  item: CampaignOutcomeItem,
  t: TFunction<'offers'>,
): string | undefined {
  switch (item.outcome) {
    case 'queued':
      return t('workspace.header.outcomeBreakdown.queuePosition', { position: item.position });
    case 'not_secured':
    case 'not_closed':
      return describeOutcomeReason(item, t);
    case 'unrecognized':
      return t('workspace.header.outcomeBreakdown.unrecognizedHint');
    default:
      return undefined;
  }
}

/** Distinct-nośnik count for one outcome — a nośnik held across several windows must
 * never count more than once for the same outcome (action-breakdown / "Counts are per
 * nośnik, not per window"). */
export function countDistinctByOutcome(
  items: CampaignOutcomeItem[],
  outcome: CampaignOutcomeItem['outcome'],
): number {
  return new Set(items.filter((item) => item.outcome === outcome).map((item) => item.mediaUnitId))
    .size;
}

export interface OutcomeGroupCount {
  outcome: CampaignOutcomeItem['outcome'];
  count: number;
}

/** Aggregate counts, one entry per outcome actually present in `items`, in display
 * order — an outcome with no entries is omitted rather than shown as zero
 * (action-breakdown / "One outcome vocabulary across fan-out actions" — "Where an
 * outcome applies to no entry, its count SHALL be omitted rather than shown as
 * zero"). */
export function groupOutcomeCounts(items: CampaignOutcomeItem[]): OutcomeGroupCount[] {
  const present = new Set(items.map((item) => item.outcome));
  return OUTCOME_DISPLAY_ORDER.filter((outcome) => present.has(outcome)).map((outcome) => ({
    outcome,
    count: countDistinctByOutcome(items, outcome),
  }));
}

/** Joins `CampaignOutcomeItem[]` against the offer's own unit list by `mediaUnitId`, so
 * the row can render the caller's configured offer-table columns. An item with no
 * matching unit is skipped defensively (the unit was removed from the offer between
 * the two reads) — the same defensive join-miss skip `useSaleBreakdown` applies. */
export function joinOutcomeRows(
  items: CampaignOutcomeItem[],
  units: OfferUnitItem[],
): OutcomeBreakdownRow[] {
  const unitsById = new Map(units.map((unit) => [unit.mediaUnitId, unit]));
  const rows: OutcomeBreakdownRow[] = [];
  for (const item of items) {
    const unit = unitsById.get(item.mediaUnitId);
    if (!unit) continue;
    rows.push({ ...unit, ...item });
  }
  return rows;
}

function OutcomeBadge({ item, t }: { item: CampaignOutcomeItem; t: TFunction<'offers'> }) {
  const detail = getOutcomeDetail(item, t);
  const looseT = t as LooseT;
  return (
    <div className="flex flex-col gap-0.5">
      <Badge variant={OUTCOME_BADGE_VARIANT[item.outcome]}>
        {looseT(OUTCOME_LABEL_KEY[item.outcome])}
      </Badge>
      {detail != null ? <span className="text-xs text-muted-foreground">{detail}</span> : null}
    </div>
  );
}

function buildLeadColumns(t: TFunction<'offers'>): ColumnDef<OutcomeBreakdownRow>[] {
  return [
    {
      id: 'outcome',
      label: t('workspace.header.outcomeBreakdown.columns.outcome'),
      align: 'left',
      width: 220,
      renderCell: (row) => <OutcomeBadge item={row} t={t} />,
    },
    {
      id: 'period',
      label: t('workspace.header.outcomeBreakdown.columns.period'),
      align: 'left',
      width: 190,
      renderCell: (row) => (
        <span className="whitespace-nowrap tabular-nums text-xs">
          {formatDisplayDateRange(row.dateFrom, row.dateTo)}
        </span>
      ),
    },
  ];
}

// Never shown in this read-only breakdown: bulk-selection (auto-omitted via
// canEdit: false below) and every cost/price column — same exclusion set
// record-sale-columns.tsx applies to its own mirrored columns, for the same reason
// (this table is about what happens to each nośnik, not what it costs).
const EXCLUDED_MIRRORED_COLUMN_IDS = new Set<string>([
  ...OFFER_UNIT_COST_COLUMN_IDS,
  ...OFFER_UNIT_PRICE_COLUMN_IDS,
]);

const NO_SELECTED_IDS: Set<OfferUnitItem['id']> = new Set();
function noopOnCheckedChange() {
  // Read-only breakdown — the checkbox column is excluded, this is never invoked.
}
function noopOnNoteBlur() {
  // Read-only breakdown — notes are not editable here.
}
function noopOnPriceChange() {
  // Read-only breakdown — price columns are excluded, this is never invoked.
}

/**
 * Builds the breakdown's full column list: the 2 outcome/period lead columns, then the
 * caller's own configured offer-table columns — same storage namespace
 * (`OFFER_TABLE_COLUMN_STORAGE_PREFIX`, via `useOfferTableColumnPreferences`) as the
 * units tab AND the record-sale dialog's own mirrored columns, so the same nośnik is
 * recognisable across all three (offer-approval / "Approval Action UI in Offer
 * Workspace").
 */
export function useOutcomeBreakdownColumns(
  t: TFunction<'offers'>,
): ColumnDef<OutcomeBreakdownRow>[] {
  const offerItemColumns = React.useMemo(
    () =>
      buildUnitsTabColumns({
        canEdit: false,
        selectedIds: NO_SELECTED_IDS,
        onCheckedChange: noopOnCheckedChange,
        onNoteBlur: noopOnNoteBlur,
        canViewSupplierDetails: true,
        price: { canEdit: false, overrides: new Map(), onPriceChange: noopOnPriceChange },
        t,
      }),
    [t],
  );
  const allColumnIds = React.useMemo(() => offerItemColumns.map((c) => c.id), [offerItemColumns]);
  const { visibleColumnIds, columnOrder, columnWidths } =
    useOfferTableColumnPreferences(allColumnIds);

  const mirroredColumns = React.useMemo(() => {
    const { visibleColumns } = buildOrderedVisibleColumns(
      offerItemColumns,
      columnOrder,
      visibleColumnIds,
      columnWidths,
    );
    return visibleColumns.filter((c) => !EXCLUDED_MIRRORED_COLUMN_IDS.has(c.id));
  }, [offerItemColumns, columnOrder, visibleColumnIds, columnWidths]);

  return React.useMemo(
    // buildUnitsTabColumns returns ColumnDef<OfferUnitItem>[]; OutcomeBreakdownRow
    // extends OfferUnitItem, and every ColumnDef<T> member uses T only in argument
    // position, so a column built for the wider OfferUnitItem type safely accepts the
    // narrower OutcomeBreakdownRow rows DataTable actually passes it here (same
    // reasoning as useRecordSaleColumns's own cast comment).
    () => [...buildLeadColumns(t), ...(mirroredColumns as ColumnDef<OutcomeBreakdownRow>[])],
    [mirroredColumns, t],
  );
}
